import { Link } from '@tanstack/react-router';
import { ExternalLink, RefreshCw } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Button } from '~/components/ui/button';
import { Skeleton } from '~/components/ui/skeleton';
import { useAuthStore } from '~/store/auth.store';
import { canAccessNavPath } from '~/utils/access';

import type { TimelineIdType } from '../../constants/enums';
import { syncStatusTone } from '../../constants/status-tones';
import { destinationOf } from '../../hooks/use-destination-copy';
import { classifyTimelineError, useProductTimeline } from '../../hooks/use-product-timeline';
import { humanizeEnum } from '../../utils/format-enum';
import { isGmcConnectorCode, isWorkspaceConnectorCode } from '../../utils/workspace-connector';
import { DeliveryStatusChip } from '../chips/delivery-status-chip';
import { IngestStatusChip } from '../chips/ingest-status-chip';
import { ProductTimelineEntryItem } from '../connection/product-timeline-entry';

import type { ProductLogSubject } from './product-delivery-log-sheet';

/** Deliveries shown per product — a product's history is a handful of jobs. */
const LOG_LIMIT = 25;

/** GMC rows are matched by offer id (`external`); every other connector by retailer SKU. */
function idTypeFor(connectorCode: string | null | undefined): TimelineIdType {
  return isGmcConnectorCode(connectorCode) ? 'external' : 'retailer';
}

interface Props {
  connectionId: string;
  connectorCode: string | null | undefined;
  subject: ProductLogSubject;
  /** Delivery job to open first (the run the user came from); else the newest. */
  focusJobId?: string | null | undefined;
  idType?: TimelineIdType | undefined;
  /** Only fetch while the host (sheet or drawer tab) is showing. */
  enabled: boolean;
  testId?: string;
}

/**
 * One product's delivery history on a connection — the body of the product delivery log, as a
 * component so the sheet and the Preview drawer's "Delivery history" tab render the same thing.
 * Shows the latest ingest / approval state, then every delivery job newest first with its diff
 * against the delivery before it. An unavailable history store says so inside this body.
 */
export function ProductDeliveryHistory({
  connectionId,
  connectorCode,
  subject,
  focusJobId,
  idType,
  enabled,
  testId = 'product-delivery-log-sheet',
}: Props) {
  const { t } = useTranslation('deliveries');
  // Same predicate as the `/deliveries` route guard, so the link never leads to a redirect.
  const canOpenDeliveries = useAuthStore((s) => canAccessNavPath('/deliveries', s.user));
  const productId = subject.productId;
  const query = useProductTimeline(
    connectionId,
    productId,
    { idType: idType ?? idTypeFor(connectorCode), limit: LOG_LIMIT, includePayload: true },
    enabled && productId !== '',
  );
  const entries = query.data?.entries ?? [];
  const latest = entries[0];
  const destination = destinationOf(connectorCode);
  const focusIndex = Math.max(
    0,
    entries.findIndex((e) => e.deliveryJobId === focusJobId),
  );
  const errorKind = query.isError ? classifyTimelineError(query.error) : null;

  return (
    <div className="space-y-4">
      {latest ? (
        <div data-testid={`${testId}-current`} className="flex flex-wrap items-center gap-2">
          <span className="text-muted-foreground text-xs">{t('productLog.sheet.current')}</span>
          <IngestStatusChip status={latest.ingestStatus} destination={destination} />
          {latest.syncStatus ? (
            <DeliveryStatusChip
              tone={syncStatusTone(latest.syncStatus)}
              label={t('productLog.sheet.approval', {
                status: humanizeEnum(latest.syncStatus),
              })}
              testId={`${testId}-approval-chip`}
            />
          ) : null}
          <span data-testid={`${testId}-delivery-count`} className="text-muted-foreground text-xs">
            {t('productLog.sheet.deliveries', {
              count: query.data?.total ?? entries.length,
            })}
          </span>
        </div>
      ) : null}

      <div className="flex flex-wrap items-center gap-2">
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="cursor-pointer gap-1.5"
          isLoading={query.isFetching}
          data-testid={`${testId}-refresh-button`}
          onClick={() => void query.refetch()}
        >
          {!query.isFetching && <RefreshCw aria-hidden="true" className="size-3.5" />}
          {t('productLog.sheet.refresh')}
        </Button>
        {/* The workspace is the Deliveries console's full per-product view (Meta + GMC). */}
        {canOpenDeliveries && isWorkspaceConnectorCode(connectorCode) ? (
          <Button variant="ghost" size="sm" asChild className="cursor-pointer gap-1.5">
            <Link
              data-testid={`${testId}-workspace-link`}
              to="/deliveries/connections/$connectionId"
              params={{ connectionId }}
              search={{ tab: 'products', sku: subject.sku ?? subject.productId }}
            >
              <ExternalLink aria-hidden="true" className="size-3.5" />
              {t('productLog.sheet.openWorkspace')}
            </Link>
          </Button>
        ) : null}
      </div>

      <p className="text-muted-foreground text-xs">{t('productLog.sheet.historyNote')}</p>

      {query.isLoading ? (
        <div data-testid={`${testId}-loading`} className="space-y-3">
          <Skeleton className="h-40 w-full" />
          <Skeleton className="h-40 w-full" />
        </div>
      ) : errorKind === 'no_history' ? (
        <p
          data-testid={`${testId}-empty`}
          className="text-muted-foreground rounded-lg border p-6 text-center text-sm"
        >
          {t('productLog.sheet.noHistory')}
        </p>
      ) : errorKind ? (
        <p
          data-testid={`${testId}-error`}
          className="text-muted-foreground rounded-lg border p-4 text-sm"
        >
          {errorKind === 'unavailable'
            ? t('productLog.sheet.unavailable')
            : t('productLog.sheet.error')}
        </p>
      ) : (
        // Keyed by product so stepping to the next one resets each card's open state.
        <ol key={productId} data-testid={`${testId}-entries`} className="space-y-0">
          {entries.map((entry, i) => (
            <ProductTimelineEntryItem
              key={`${entry.deliveryJobId}-${String(i)}`}
              entry={entry}
              payloadRequested
              isLast={i === entries.length - 1}
              defaultOpen={i === focusIndex}
              previousEntry={entries[i + 1]}
            />
          ))}
        </ol>
      )}
      {query.data?.truncated ? (
        <p data-testid={`${testId}-truncated`} className="text-muted-foreground text-xs">
          {t('productLog.sheet.truncated', { count: entries.length })}
        </p>
      ) : null}
    </div>
  );
}
